
import { supabase } from '@/integrations/supabase/client';
import { Rental } from '@/services/types';
import { mockRentals } from '@/services/mockData';
import { fetchTableData } from '@/integrations/supabase/tableUtils';
import { 
  fetchRentalsWithClient,
  fetchRentalsWithRest
} from './rentalFetchApi';
import { mapRentalsData } from './rentalMapper';

export interface RentalDataResult {
  rentals: Rental[];
  totalCount: number;
  dataSource: 'supabase' | 'mock';
  connectionError: string | null;
}

/**
 * Получает общую выручку по завершенным платежам (completed и paid)
 */
export const fetchRevenueFromPayments = async (): Promise<{
  totalRevenue: number;
  connectionError: string | null;
}> => {
  try {
    console.log('Получение выручки из завершенных платежей...');
    
    const { data: paymentsData, error } = await supabase
      .from('payments')
      .select('amount')
      .in('status', ['completed', 'paid']);
    
    if (error) {
      console.error('Ошибка при получении платежей для расчета выручки:', error);
      throw error;
    }

    const totalRevenue = paymentsData?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
    
    console.log(`Рассчитана общая выручка из ${paymentsData?.length || 0} завершенных платежей: ${totalRevenue}`);
    
    return {
      totalRevenue,
      connectionError: null
    };
  } catch (error: any) {
    console.error('Ошибка при получении выручки из платежей:', error);
    return {
      totalRevenue: 0,
      connectionError: error.message
    };
  }
};

/**
 * Получает все данные о арендах из Supabase или мок-данные без пагинации
 */
export const fetchAllRentals = async (): Promise<RentalDataResult> => {
  let rentalsData = null;
  let bikesData = null;
  let couriersData = null;
  let error = null;
  let totalCount = 0;

  try {
    console.log('Получение всех данных об арендах...');

    // Пробуем загрузить данные через клиент Supabase с JOIN запросом
    console.log('Попытка загрузки через клиент Supabase с JOIN...');
    const clientResult = await fetchRentalsWithClient();
    
    if (!clientResult.error && clientResult.data && clientResult.data.length > 0) {
      rentalsData = clientResult.data;
      totalCount = rentalsData.length;
      console.log('Успешно загружены данные через клиент Supabase с JOIN:', rentalsData.length);
      
      // Логируем пример данных для проверки структуры
      if (rentalsData.length > 0) {
        console.log('Пример данных аренды с JOIN:', {
          id: rentalsData[0].id,
          status: rentalsData[0].status,
          courier: rentalsData[0].couriers,
          bike: rentalsData[0].bikes,
          courier_id: rentalsData[0].courier_id,
          bike_id: rentalsData[0].bike_id
        });
      }
    } else {
      console.log('Не удалось загрузить данные через клиент Supabase:', clientResult.error);
      
      // Пробуем загрузить через REST API с JOIN
      console.log('Попытка загрузки через REST API с JOIN...');
      const restResult = await fetchRentalsWithRest();
      
      if (!restResult.error && restResult.data && restResult.data.length > 0) {
        rentalsData = restResult.data;
        totalCount = rentalsData.length;
        console.log('Успешно загружены данные через REST API с JOIN:', rentalsData.length);
        
        // Логируем пример данных для проверки структуры
        if (rentalsData.length > 0) {
          console.log('Пример данных аренды с JOIN:', {
            id: rentalsData[0].id,
            status: rentalsData[0].status,
            courier: rentalsData[0].couriers,
            bike: rentalsData[0].bikes,
            courier_id: rentalsData[0].courier_id,
            bike_id: rentalsData[0].bike_id
          });
        }
      } else {
        console.log('Не удалось загрузить данные через REST API:', restResult.error);
        
        // Fallback на старый метод без JOIN
        console.log('Fallback: загрузка данных без JOIN...');
        const tableResult = await fetchTableData('rentals');
        
        if (tableResult.success && tableResult.data && tableResult.data.length > 0) {
          rentalsData = tableResult.data;
          totalCount = rentalsData.length;
          console.log('Успешно загружены данные через fallback (без JOIN):', rentalsData.length);
        } else {
          error = tableResult.message || 'Не удалось загрузить данные аренд из Supabase';
          console.error('Все методы загрузки данных аренд завершились с ошибкой:', error);
        }
      }
    }
    
    // Если JOIN не работает или данные отсутствуют, загружаем данные отдельно
    if (rentalsData && (!rentalsData[0]?.couriers || !rentalsData[0]?.bikes)) {
      console.log('JOIN данные отсутствуют, загружаем велосипеды и курьеров отдельно...');
      
      // Загружаем данные велосипедов
      const bikesResult = await fetchTableData('bikes');
      if (bikesResult.success && bikesResult.data) {
        bikesData = bikesResult.data;
        console.log('Загружено велосипедов отдельно:', bikesData.length);
      }
      
      // Загружаем данные курьеров
      const couriersResult = await fetchTableData('couriers');
      if (couriersResult.success && couriersResult.data) {
        couriersData = couriersResult.data;
        console.log('Загружено курьеров отдельно:', couriersData.length);
      }
    }

    // Маппинг данных с учетом отдельно загруженных данных
    const formattedRentals = rentalsData 
      ? mapRentalsData(rentalsData, bikesData, couriersData)
      : [];

    console.log(`Обработано ${formattedRentals.length} аренд`);
    return {
      rentals: formattedRentals,
      totalCount,
      dataSource: 'supabase',
      connectionError: null
    };

  } catch (error: any) {
    console.error('Ошибка при получении данных о арендах, возвращаем мок-данные:', error);
    
    return {
      rentals: mockRentals,
      totalCount: mockRentals.length,
      dataSource: 'mock',
      connectionError: error.message
    };
  }
};

// Оставляем старые функции для обратной совместимости
export const fetchRentalsWithPagination = fetchAllRentals;
export const fetchRentalsStats = fetchAllRentals;
